import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectGameType,
  selectPendingMeleeDamage,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage,
} from '../redux/reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../constants/game';
import {PHASES, WAR_PHASE_STAGES} from '../constants/phases';
import {NEUTRAL_PLAYER_ID} from '../constants/players';
import {MELEE_ANIMATION, resolveMeleeWeaponFrame} from '../configs/meleeAnimation';
import {
  BOT_COMBAT_ANIMATED_UP_TO_SPEED_MULTIPLIER,
  FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER,
} from '../configs/playbackSpeed';
import {BOT_ACTION_INTERVAL_MS} from '../utils/bots/botConfig';
import {calcUnitMeleeDamageWithFortress} from '../utils/damage/calcUnitReceivedDamage';
import {
  assignMeleeStrikeLosses,
  collectMeleeHits,
  planMeleeTimeline,
} from '../utils/effects/buildMeleeTimeline';
import {getUnitDeathPauseMs} from '../utils/effects/unitDeathPause';
import {checkIfUnitHasFactionSkin} from '../utils/factions/checkIfUnitHasFactionSkin';
import {getPlayer0xColor} from '../utils/players/getPlayer0xColor';
import {checkIfReplayRewinding, getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {resolveShotPaceMs} from './playShotAnimation';

let lastAnimationNumber = 0;

/**
 * Анимация ударов ближнего боя (см. HexGridRenderer.showMeleeAnimation).
 * Зовется ПОСЛЕ того, как экшен расчета боя CALCULATE_MELEE_COMBAT_DAMAGE
 * применен, — из одного места, middleware стора (см.
 * redux/middleware/meleeAnimationMiddleware): расчет приходит в стор десятком
 * путей (кнопка завершения этапа, клик по приоритетной цели, переброс костей,
 * сеть, боты, запись матча, таймер хода), и хуки в каждом из них разъехались
 * бы.
 *
 * Экшен один на три события, различает их payload:
 *  - вход в этап (ни цели, ни переброса) — бой целиком: сначала бьют отряды
 *    игрока, чей сейчас ход, потом отряды остальных;
 *  - выбор приоритетной цели (priorityTargetForUnit) — заново бьет только
 *    этот отряд, по всем целям, которым он теперь наносит урон. Цель могла
 *    остаться прежней — удары все равно повторяются;
 *  - переброс костей (isReroll, rerollUnitId) — так же, после паузы на звук
 *    костей.
 *
 * ПОВТОР ПОВЕРХ ИДУЩЕГО БОЯ. Бот-защитник и соперник по сети выбирают цель и
 * перебрасывают кости, пока удары боя еще идут. Повтор встает в очередь за
 * ними (см. HexGridRenderer.getMeleeQueueMs), а удары, которые еще не начались,
 * сверяются с новым расчетом (см. syncPendingStrikes) — в том числе когда
 * своей анимации у пересчета нет.
 *
 * ЗАПИСЬ МАТЧА (страница записи и витрина главного меню). До x5 удары идут
 * целиком и с обычной длительностью, как в
 * своем бою: проигрыватель записи не исполняет следующее действие, пока
 * анимация не уйдет с поля (см. ReplayRunner), — шаг воспроизведения ее не
 * поджимает. Быстрее x5 запись — уже перемотка: анимации нет.
 *
 * Анимации нет, а итог боя виден сразу, как раньше:
 *  - в записи матча быстрее x5 и при ботах быстрее x2 (у ботов — то же
 *    правило, что у выстрела);
 *  - при перемотке записи матча (шаг назад прогоняет всю историю заново);
 *  - когда чужой бой в живой партии не укладывается в шаг темпа даже сжатым
 *    (см. pace);
 *  - в скрытой вкладке — кадры там не идут;
 *  - когда экшен не применился или бить некому (урон никому не нанесен).
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {Object} [params.payload] — payload экшена расчета боя
 * @param {Array} [params.previousPendingMeleeDamage] — записи боя до экшена
 * @param {boolean} [params.isFromServer] — экшен пришел по сети
 * @returns {boolean} пошла ли анимация
 */
export const playMeleeAnimation = (
  store, hexGridRenderer, {payload, previousPendingMeleeDamage, isFromServer = false} = {}
) => {
  if (!hexGridRenderer || typeof hexGridRenderer.showMeleeAnimation !== 'function') {
    return false;
  }
  if (checkIfReplayRewinding()) {
    return false;
  }

  const state = store.getState();
  if (selectPhase(state) !== PHASES.WAR || selectWarPhaseStage(state) !== WAR_PHASE_STAGES.COMBAT) {
    return false;
  }
  const pendingMeleeDamage = selectPendingMeleeDamage(state) || [];
  // Редьюсер на каждый расчет собирает записи заново: тот же массив — значит,
  // экшен отброшен (устаревшее действие, блокировка ввода)
  if (pendingMeleeDamage.length === 0 || pendingMeleeDamage === previousPendingMeleeDamage) {
    return false;
  }

  const replay = resolveMeleeReplay(payload, pendingMeleeDamage);
  if (replay === null) {
    return false;
  }

  const units = selectUnits(state) || [];
  const unitsById = new Map(units.map((unit) => [unit.id, unit]));
  const resolveSizeLoss = createSizeLossResolver(state, unitsById);
  const plan = planMeleeReplay({
    state, hexGridRenderer, replay, pendingMeleeDamage, units, unitsById, resolveSizeLoss, isFromServer,
  });
  if (replay.attackerUnitIds) {
    syncPendingStrikes(hexGridRenderer, {
      pendingMeleeDamage,
      resolveSizeLoss,
      // Отряды повтора ударят после всех: их урон не начатым ударам не база
      laterAttackerUnitIds: plan ? replay.attackerUnitIds : [],
    });
  }
  if (!plan) {
    return false;
  }

  lastAnimationNumber += 1;
  try {
    return hexGridRenderer.showMeleeAnimation({
      id: `melee_${lastAnimationNumber}`,
      ...plan,
      units,
      round: selectRound(state),
      isFullBattle: !replay.attackerUnitIds,
      // Взмахи звучат в момент начала ударов — по часам самой анимации
      onStrikesStart: (startedStrikes) => {
        hexGridRenderer.scene?.soundManager?.playMeleeStrikes?.(startedStrikes.map((strike) => strike.damage));
      },
    });
  } catch (error) {
    // Анимация — украшение: ее поломка не должна ронять разбор экшена
    console.warn('[Melee] не удалось показать удары', error);
    return false;
  }
};

/**
 * Что проигрывать по экшену расчета боя.
 *
 * @returns {{attackerUnitIds?: string[], delayMs: number}|null} attackerUnitIds
 *   не задан — бой целиком; null — проигрывать нечего (переброс не применился)
 */
const resolveMeleeReplay = (payload, pendingMeleeDamage) => {
  const {isReroll, rerollUnitId, priorityTargetForUnit} = payload || {};
  if (isReroll) {
    // Отброшенный переброс узнается по hasRerolled: его выставляет только
    // примененный экшен
    const record = pendingMeleeDamage.find((item) => item.unitId === rerollUnitId);
    return record?.hasRerolled ? {attackerUnitIds: [rerollUnitId], delayMs: MELEE_ANIMATION.rerollDelayMs} : null;
  }
  const priorityUnitIds = Object.keys(priorityTargetForUnit || {});
  if (priorityUnitIds.length > 0) {
    return {attackerUnitIds: priorityUnitIds, delayMs: 0};
  }
  return {delayMs: 0};
};

/**
 * План анимации для HexGridRenderer.showMeleeAnimation: расписание ударов с
 * числами, цветами и оружием, пауза до первого удара и придержанные отряды.
 *
 * @returns {Object|null} null — своей анимации у этого расчета нет (см. список
 *   в описании playMeleeAnimation)
 */
const planMeleeReplay = ({
  state, hexGridRenderer, replay, pendingMeleeDamage, units, unitsById, resolveSizeLoss, isFromServer,
}) => {
  if (typeof document !== 'undefined' && document.hidden) {
    return null;
  }
  const hits = collectMeleeHits(pendingMeleeDamage, {attackerUnitIds: replay.attackerUnitIds});
  if (hits.length === 0) {
    return null;
  }

  const players = selectPlayers(state) || [];
  const currentPlayer = selectCurrentPlayer(state);
  // Кто задает темп: в бою целиком — игрок, чей сейчас ход; у повтора — владелец отряда
  const actingOwnerId = replay.attackerUnitIds
    ? unitsById.get(replay.attackerUnitIds[0])?.ownerId
    : currentPlayer?.id;
  const actingOwner = players.find((player) => player.id === actingOwnerId);
  // Запись матча удары не поджимает: проигрыватель ждет их конца сам (см.
  // ReplayRunner). Пометки ботов у игроков записи остаются (их видит админ) —
  // темпа они здесь не задают
  const isReplay = selectGameType(state) === GAME_TYPES.REPLAY;
  if (isReplay && getReplayPlaybackSpeedMultiplier() > FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER) {
    return null;
  }
  const isPaced = !isReplay && (isFromServer || Boolean(actingOwner?.isBot));
  const paceMs = isPaced ? resolveShotPaceMs(state, actingOwnerId) : undefined;
  // Боты быстрее x2 — анимации нет: то же правило, что у перемещения и выстрела
  if (paceMs && BOT_ACTION_INTERVAL_MS / paceMs > BOT_COMBAT_ANIMATED_UP_TO_SPEED_MULTIPLIER) {
    return null;
  }

  // Повтор ждет, пока отыграют идущие удары (их конец — его пауза), и вместе
  // с этим ожиданием должен уложиться в шаг чужого темпа. Удары, которые по
  // новому расчету урона не наносят, сверка снимет — их он не ждет
  const checkIfStrikeStays = (strike) => resolvePairDamage(pendingMeleeDamage, strike) > 0;
  const queueMs = replay.attackerUnitIds
    ? hexGridRenderer.getMeleeQueueMs?.(replay.attackerUnitIds, checkIfStrikeStays) || 0
    : 0;
  let delayMs = Math.max(replay.delayMs, queueMs);
  // Бой целиком сразу за гибелью от стрельбы ждет ее вспышку. Не укладывается с этой
  // паузой в шаг чужого темпа — идет без нее: лучше удары поверх гибели, чем без ударов
  const deathPauseMs = replay.attackerUnitIds ? 0 : getUnitDeathPauseMs();
  let timeline = planMeleeTimeline({hits, firstOwnerId: currentPlayer?.id, paceMs, delayMs: Math.max(delayMs, deathPauseMs)});
  if (timeline) {
    delayMs = Math.max(delayMs, deathPauseMs);
  } else if (deathPauseMs > 0) {
    timeline = planMeleeTimeline({hits, firstOwnerId: currentPlayer?.id, paceMs, delayMs});
  }
  if (!timeline) {
    return null;
  }

  // Витрина главного меню рисует особыми образами все отряды игроков — тем же
  // правилом выбирается и оружие (см. HexGridRenderer.resolveUnitImageKey)
  const isEverySkinShown = Boolean(hexGridRenderer.scene?.isDemoMode);
  const strikes = assignMeleeStrikeLosses(timeline.strikes, {
    resolveSizeLoss,
    baseDamageByTarget: sumDamageByTarget(
      pendingMeleeDamage.filter((record) => replay.attackerUnitIds && !replay.attackerUnitIds.includes(record.unitId))
    ),
  }).map((strike) => {
    const attacker = unitsById.get(strike.attackerUnitId);
    const owner = players.find((player) => player.id === attacker?.ownerId);
    const hasFactionSkin = checkIfUnitHasFactionSkin(attacker, players, {isEverySkinShown});
    return {
      ...strike,
      color: resolveMeleeEffectColor(attacker, owner),
      // Оружие фракции — только у отряда с ее особым образом: отряд, который
      // нарисован общим силуэтом (образа у игрока нет, нейтральный отряд),
      // бьет стандартным мечом
      weaponFrame: resolveMeleeWeaponFrame(hasFactionSkin ? attacker.factionId : null),
    };
  });

  return {
    strikes,
    totalMs: timeline.totalMs,
    revealAtMs: timeline.revealAtMs,
    timeFactor: timeline.timeFactor,
    delayMs,
    // Чей итог боя придержан до конца ударов: в бою целиком — всех участников,
    // у повтора — отряда и тех, по кому он бьет
    heldUnitIds: [...new Set([
      ...(replay.attackerUnitIds || pendingMeleeDamage.map((record) => record.unitId)),
      ...strikes.map((strike) => strike.targetUnitId),
    ])],
  };
};

/**
 * Удары идущих анимаций, которые еще не начались, — по новому расчету боя.
 * Пересчет (приоритетная цель, переброс) заново делит урон ВСЕХ отрядов:
 * общий остаток цели распределяется между бьющими, и урон меняется не только
 * у отряда, который выбирал. Удар, не успевший начаться, должен прозвучать и
 * показать «−N» по новому расчету; удар, от которого урона не осталось, —
 * не состояться.
 *
 * Числа считаются тем же правилом, что и при запуске анимации: потеря, которую
 * добавил именно этот удар. Базой служит урон по цели от всех остальных — от
 * ударов, которые уже начались (их числа показаны), и от отрядов без удара в
 * очереди; не начатые удары досчитывают до числа на плашке.
 *
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {Array} params.pendingMeleeDamage — записи боя после пересчета
 * @param {Function} params.resolveSizeLoss
 * @param {string[]} params.laterAttackerUnitIds — отряды, чьи удары заменит
 *   повтор: он встанет в очередь после всех, поэтому их не начатые удары
 *   снимаются, а их урон в базу остальных не входит
 */
const syncPendingStrikes = (hexGridRenderer, {pendingMeleeDamage, resolveSizeLoss, laterAttackerUnitIds}) => {
  try {
    hexGridRenderer.updatePendingMeleeStrikes?.((pendingStrikes) => {
      const strikes = pendingStrikes.map((strike, index) => ({
        ...strike,
        // Порядок ударов задает рендерер: считаем числа в нем же
        startAtMs: index,
        damage: laterAttackerUnitIds.includes(strike.attackerUnitId)
          ? 0
          : resolvePairDamage(pendingMeleeDamage, strike),
      }));

      const baseDamageByTarget = sumDamageByTarget(
        pendingMeleeDamage.filter((record) => !laterAttackerUnitIds.includes(record.unitId))
      );
      strikes.forEach((strike) => {
        baseDamageByTarget[strike.targetUnitId] = Math.max(
          0, (baseDamageByTarget[strike.targetUnitId] || 0) - strike.damage
        );
      });

      return assignMeleeStrikeLosses(strikes, {resolveSizeLoss, baseDamageByTarget});
    });
  } catch (error) {
    // Сверка — украшение: ее поломка не должна ронять разбор экшена
    console.warn('[Melee] не удалось сверить идущие удары с новым расчетом', error);
  }
};

/** Урон отряда по цели в записях боя (0 — урона или самой записи нет) */
const resolvePairDamage = (pendingMeleeDamage, {attackerUnitId, targetUnitId}) => (
  pendingMeleeDamage.find((record) => record.unitId === attackerUnitId)?.damageDistribution?.[targetUnitId] || 0
);

const sumDamageByTarget = (records) => {
  const damageByTarget = {};
  records.forEach((record) => Object.entries(record.damageDistribution || {}).forEach(([targetUnitId, damage]) => {
    damageByTarget[targetUnitId] = (damageByTarget[targetUnitId] || 0) + damage;
  }));
  return damageByTarget;
};

/**
 * Потеря численности цели при заданном суммарном уроне по ней — тем же
 * расчетом, по которому рисуется «−N» на плашке (орки, крепость), и не больше
 * размера отряда
 */
const createSizeLossResolver = (state, unitsById) => {
  const specialBuildings = selectSpecialBuildings(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const fortressesUnderConstruction = selectFortressesUnderConstruction(state);

  return (targetUnitId, damage) => {
    const unit = unitsById.get(targetUnitId);
    if (!unit || !(damage > 0)) {
      return 0;
    }
    const {sizeLoss} = calcUnitMeleeDamageWithFortress(
      unit, [{ownerId: null, damageDistribution: {[unit.id]: damage}}],
      specialBuildings, buildingsDamage, fortressesUnderConstruction
    );
    return Math.min(unit.size, sizeLoss);
  };
};

/**
 * Цвет эффектов удара: цвет игрока-владельца бьющего отряда. У нейтрального
 * отряда «цвет игрока» — чистый черный: черные след и вспышка не читаются
 */
const resolveMeleeEffectColor = (attacker, owner) => (
  !owner || attacker?.ownerId === NEUTRAL_PLAYER_ID
    ? MELEE_ANIMATION.neutralColor
    : getPlayer0xColor(owner.color)
);
